'use client';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { loginUser } from '@/services/userSessionService';
import { selectIsLoggedIn, selectResetPasswordRequired, useUserSessionStore } from '@/stores/userSessionStore';
import { EntityStoreStatuses, selectStatus } from '@/util/createFetchStore';
import { AlertCircle } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { handleException } from '~/modules/utilities/cross_env_utils';

// Dummy credentials for demo
const DEMO_EMAIL = 'test@warrantypilot.com';
const DEMO_PASSWORD = 'Password in Vaultwarden';

export function LoginComponent() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const status = useUserSessionStore(selectStatus);
  const isLoading = status === EntityStoreStatuses.LOADING;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const message = await loginUser({
        email,
        password,
      });

      if (selectResetPasswordRequired()) {
        router.push('/account/change-password');
      } else if (selectIsLoggedIn()) {
        router.push('/account');
      } else if (message) {
        throw new Error(message);
      } else {
        throw new Error('User not logged in');
      }
    } catch (error) {
      handleException(error);
      setError(error.message || 'There was an issue logging in');
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Login to Your Account</CardTitle>
          <CardDescription>Access your warranty plan and manage your coverage</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                minLength={10}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <div className="flex items-end justify-end">
                <Link href="/forgot-password" className="text-sm text-[#00B67A] hover:underline">
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button type="submit" className="w-full bg-[#00B67A] hover:bg-[#00A067]" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>

            <div className="text-center text-sm text-gray-600">
              Don&apos;t have a plan yet?{' '}
              <Link href="/" className="text-[#00B67A] hover:underline">
                Browse Available Plans
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
